<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $tc('messages.gamification_sets', 2),
                        href: meta.links.set_index,
                    },
                    {
                        name: gamificationSet.breadcrumb_title,
                        href: meta.links.set_show,
                    },
                    {
                        name: $tc('messages.itineraries', 2),
                        href: meta.links.index,
                    },
                    {
                        name: itinerary.name,
                        href: null,
                    },
                ]"
            />
        </template>
        <div class="md:grid md:grid-cols-3 md:gap-6">
            <div class="md:col-span-1 hidden sm:block">
                <div style="aspect-ratio: 9 / 16" class="w-full relative bg-gray-100 overflow-y-scroll rounded-lg">
                    <div
                        class="w-full absolute left-0 top-0 px-2 min-h-full"
                        style="background-repeat: repeat-y; background-size: 100% auto"
                        :style="`background-image: url(${itinerary.background_image_path})`"
                    >
                        <transition-group type="transition" :name="!drag ? 'flip-list' : null">
                            <div
                                v-for="station in previewStations"
                                class="grid grid-cols-5 list-group-item"
                                :key="station.id"
                            >
                                <div style="order: 1">&nbsp;</div>
                                <div style="order: 2">&nbsp;</div>
                                <div style="order: 3">&nbsp;</div>
                                <div style="order: 4">&nbsp;</div>
                                <div
                                    class="aspect-square inline-block drop-shadow-xl"
                                    :style="`order: ${station.position}`"
                                    :title="station.type_display"
                                >
                                    <TreasureChest
                                        v-if="station.type === 'gamification_chest'"
                                        class=""
                                        :type="station.settings.type"
                                        :is-open="false"
                                    />
                                    <img
                                        v-else-if="station.type === 'final'"
                                        class="w-full h-full object-contain"
                                        :src="station.image"
                                        alt="final station"
                                    />
                                    <StationBase
                                        v-else
                                        :color="itinerary.base_color"
                                        :shape="itinerary.base_shape"
                                        style="transform: rotateX(45deg); transform-style: preserve-3d"
                                        with-shadow
                                    >
                                        <StationIcon :type="station.type" class="w-1/2 h-1/2 text-white opacity-50" />
                                    </StationBase>
                                </div>
                            </div>
                        </transition-group>
                    </div>
                </div>
            </div>
            <div class="md:col-span-2">
                <div>{{ itinerary.published_at }} - {{ itinerary.unpublished_at }}</div>
                <div>{{ itinerary.time_start }} - {{ itinerary.time_end }}</div>
                <div>{{ itinerary.group_names }}</div>
                <div>
                    <InertiaLink :href="meta.links.itinerary_students_show" class="text-indigo-500">
                        {{ itinerary.student_itineraries_count }} / {{ itinerary.total_students_count }}
                        {{ $tc('messages.students', 2) }}
                    </InertiaLink>
                </div>

                <AppWell v-if="itinerary.description" class="mt-5">
                    <AppCard>
                        <p class="p-4 whitespace-pre-line text-sm">{{ itinerary.description }}</p>
                    </AppCard>
                </AppWell>

                <div class="mt-5 min-h-screen">
                    <div class="flex space-x-2">
                        <span>
                            {{ $tc('messages.itinerary_stations', 2) }}
                        </span>
                    </div>
                    <AppWell class="mt-3 space-y-0.5">
                        <draggable
                            tag="ul"
                            v-model="stations"
                            group="stations"
                            @start="drag = true"
                            @end="drag = false"
                            handle=".handle"
                            @change="reorderStations()"
                            item-key="id"
                            class=""
                        >
                            <template #item="{ element: station, index }">
                                <li class="list-group-item">
                                    <AppCard class="mt-1">
                                        <div class="flex justify-between items-center gap-x-1 sm:gap-x-2">
                                            <div class="flex-none w-4 inline-block flex-none">
                                                <IconGripVertical
                                                    class="handle inline-block h-4 w-4 text-gray-600 cursor-move"
                                                />
                                            </div>
                                            <div
                                                class="flex flex-col sm:flex-row justify-between align-center items-center gap-x-2 flex-none w-10 sm:w-16"
                                            >
                                                <div
                                                    class="w-8 h-8 sm:w-12 sm:h-12"
                                                    v-if="station.type === 'gamification_chest'"
                                                >
                                                    <TreasureChest
                                                        :type="station.settings.type"
                                                        class="object-contain"
                                                        :is-open="false"
                                                    />
                                                </div>
                                                <div
                                                    class="w-8 h-8 sm:w-12 sm:h-12"
                                                    v-else-if="station.type === 'final'"
                                                >
                                                    <img
                                                        class="w-full h-full object-contain"
                                                        :src="station.image"
                                                        alt="final station"
                                                    />
                                                </div>
                                                <StationBase
                                                    v-else
                                                    :color="itinerary.base_color"
                                                    :shape="itinerary.base_shape"
                                                    class="w-8 h-8 sm:w-12 sm:h-12"
                                                >
                                                    <StationIcon
                                                        :type="station.type"
                                                        class="w-1/2 h-1/2 text-white opacity-50"
                                                    />
                                                </StationBase>

                                                <span class="font-mono text-sm text-gray-400 tabular-nums">
                                                    {{ String(index + 1).padStart(2, '0') }}
                                                </span>
                                            </div>
                                            <div class="flex-1 flex flex-col sm:flex-row gap-x-1 sm:gap-x-2 min-w-0">
                                                <div class="flex-1 min-w-0 truncate text-sm sm:text-base">
                                                    <span v-if="station.links.stationable_url">
                                                        <a
                                                            :href="station.links.stationable_url"
                                                            class="text-indigo-500"
                                                        >
                                                            {{ station.display_name }}
                                                        </a>
                                                    </span>
                                                    <span v-else>{{ station.display_name }}</span>
                                                    <div v-if="station.students_count">
                                                        <Tippy>
                                                            <IconArrowAutofitRight
                                                                class="w-6 text-gray-400 inline-block"
                                                            />
                                                            <template #content>
                                                                {{
                                                                    $tc(
                                                                        'messages.limited_to_X_students',
                                                                        station.students_count,
                                                                        {
                                                                            count: station.students_count,
                                                                        },
                                                                    )
                                                                }}
                                                            </template>
                                                        </Tippy>
                                                    </div>
                                                </div>
                                                <div class="flex-none flex items-center gap-x-1 sm-gap-x-2 justify-end">
                                                    <div
                                                        class="flex-none flex justify-between items-center"
                                                        v-if="station.unlocked_at_display"
                                                    >
                                                        <Tippy>
                                                            <svg
                                                                v-if="station.is_unlocked"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                class="w-4 h-4 text-gray-400 icon icon-tabler icon-tabler-lock-open"
                                                                width="24"
                                                                height="24"
                                                                viewBox="0 0 24 24"
                                                                stroke-width="2"
                                                                stroke="currentColor"
                                                                fill="none"
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                            >
                                                                <path
                                                                    stroke="none"
                                                                    d="M0 0h24v24H0z"
                                                                    fill="none"
                                                                ></path>
                                                                <rect x="5" y="11" width="14" height="10" rx="2"></rect>
                                                                <circle cx="12" cy="16" r="1"></circle>
                                                                <path d="M8 11v-5a4 4 0 0 1 8 0"></path>
                                                            </svg>
                                                            <svg
                                                                v-else
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                class="w-4 h-4 text-gray-400 inline-block icon icon-tabler icon-tabler-lock"
                                                                viewBox="0 0 24 24"
                                                                stroke-width="2"
                                                                stroke="currentColor"
                                                                fill="none"
                                                                stroke-linecap="round"
                                                                stroke-linejoin="round"
                                                            >
                                                                <path
                                                                    stroke="none"
                                                                    d="M0 0h24v24H0z"
                                                                    fill="none"
                                                                ></path>
                                                                <rect x="5" y="11" width="14" height="10" rx="2"></rect>
                                                                <circle cx="12" cy="16" r="1"></circle>
                                                                <path d="M8 11v-4a4 4 0 0 1 8 0v4"></path>
                                                            </svg>

                                                            <template #content>
                                                                {{ station.unlocked_at_display }}
                                                            </template>
                                                        </Tippy>
                                                        <div class="text-gray-500 text-sm">
                                                            {{ station.unlocked_at_display }}
                                                        </div>
                                                    </div>
                                                    <div
                                                        class="w-10 flex-none"
                                                        v-if="station.student_itineraries_count"
                                                    >
                                                        <Tippy>
                                                            <button>
                                                                <div class="flex items-center text-gray-400 text-xs">
                                                                    <svg
                                                                        fill="currentColor"
                                                                        class="h-4 w-4"
                                                                        viewBox="0 0 20 20"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                        aria-hidden="true"
                                                                    >
                                                                        <path
                                                                            d="M10 9a3 3 0 100-6 3 3 0 000 6zM6 8a2 2 0 11-4 0 2 2 0 014 0zM1.49 15.326a.78.78 0 01-.358-.442 3 3 0 014.308-3.516 6.484 6.484 0 00-1.905 3.959c-.023.222-.014.442.025.654a4.97 4.97 0 01-2.07-.655zM16.44 15.98a4.97 4.97 0 002.07-.654.78.78 0 00.357-.442 3 3 0 00-4.308-3.517 6.484 6.484 0 011.907 3.96 2.32 2.32 0 01-.026.654zM18 8a2 2 0 11-4 0 2 2 0 014 0zM5.304 16.19a.844.844 0 01-.277-.71 5 5 0 019.947 0 .843.843 0 01-.277.71A6.975 6.975 0 0110 18a6.974 6.974 0 01-4.696-1.81z"
                                                                        ></path>
                                                                    </svg>
                                                                    <div>
                                                                        &times;<span class="text-sm">{{
                                                                            station.student_itineraries_count
                                                                        }}</span>
                                                                    </div>
                                                                </div>
                                                            </button>

                                                            <template #content>
                                                                <ul class="text-sm p-1">
                                                                    <li
                                                                        v-for="student in station.currentStudents"
                                                                        :key="student.id"
                                                                    >
                                                                        {{ student.full_name }}
                                                                    </li>
                                                                </ul>
                                                            </template>
                                                        </Tippy>
                                                    </div>
                                                    <div class="flex justify-end gap-x-2 items-center">
                                                        <div class="flex gap-x-1 items-center">
                                                            <InertiaSquareLinkButton
                                                                action="show"
                                                                :href="station.links.show"
                                                            />
                                                            <InertiaSquareLinkButton
                                                                action="edit"
                                                                :href="station.links.edit"
                                                            />
                                                            <InertiaSquareDeleteButton
                                                                :href="station.links.delete"
                                                                v-if="station.links.delete"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </AppCard>
                                </li>
                            </template>
                        </draggable>
                    </AppWell>
                    <div class="mt-3 py-5">
                        <div class="inline-flex rounded-md shadow-sm absolute">
                            <DropdownButton class="relative -ml-px block">
                                <template v-slot:text>
                                    <svg
                                        class="w-6 h-6"
                                        fill="currentColor"
                                        viewBox="0 0 20 20"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            fill-rule="evenodd"
                                            d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                                            clip-rule="evenodd"
                                        ></path>
                                    </svg>
                                </template>
                                <template v-slot:content>
                                    <InertiaLink
                                        v-for="link in meta.links.station_create_links"
                                        :key="link.url"
                                        :href="link.url"
                                        class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                        role="menuitem"
                                    >
                                        {{ link.text }}
                                    </InertiaLink>
                                </template>
                            </DropdownButton>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import StationBase from '@/components/Forms/StationBase.vue';
import AppWell from '@/Shared/AppWell.vue';
import AppCard from '@/Shared/AppCard.vue';
import DropdownButton from '@/components/DropdownButton.vue';
import StationIcon from '@/components/Forms/StationIcon.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import draggable from 'vuedraggable';
import axios from 'axios';
import { Tippy } from 'vue-tippy';
import TreasureChest from '@/components/TreasureChest.vue';
import { IconArrowAutofitRight, IconBounceRight, IconGripVertical, IconSTurnUp } from '@tabler/icons-vue';
import BadgePill from '@/components/BadgePill.vue';

const stationPosition = (i) => {
    const map = {
        0: 0,
        1: 1,
        2: 2,
        3: 3,
        4: 4,
        5: 3,
        6: 2,
        7: 1,
    };

    return map[(i + 2) % 8];
};

export default {
    components: {
        BadgePill,
        TreasureChest,
        InertiaSquareLinkButton,
        InertiaSquareDeleteButton,
        StationIcon,
        PanelPage,
        StationBase,
        AppWell,
        AppCard,
        DropdownButton,
        draggable,
        Tippy,
        IconArrowAutofitRight,
        IconBounceRight,
        IconSTurnUp,
        IconGripVertical,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'gamificationSet', 'options', 'itinerary'],
    methods: {
        reorderStations() {
            const newOrder = this.stations.map((s) => s.id);

            axios.patch(this.meta.links.reorder_stations, {
                stations: newOrder,
            });
        },
    },
    data() {
        return {
            stations: this.itinerary.stations,
            drag: false,
        };
    },
    computed: {
        previewStations: function () {
            return this.stations.map((s, index) => {
                s.position = stationPosition(index);
                return s;
            });
        },
    },
};
</script>
<style scoped>
.flip-list-move {
    transition: transform 0.5s;
}
.no-move {
    transition: transform 0s;
}
.ghost {
    opacity: 0.2;
    background-color: #c7d2fe;
}
.list-group {
    min-height: 20px;
}
</style>
